// What the treemap's view, canvas and panel share: how a class reads, and
// the selection of Explore that reproduces its count where one does.

import { TreemapNode } from "../../../types/analytics";
import { AnalyticsSelection, selectionOf } from "../analyticsState";
import { TreemapRect } from "./treemapLayout";

export const counted = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

export const ROOT_OTHER_ID = "other";

// The products under `other` at the top: filed on no class at all
export const isRootOther = (node: TreemapNode): boolean =>
  node.other && node.id === ROOT_OTHER_ID;

// The products under `other` beside a class's own classes: filed on the
// class and under none of them
export const isBranchOther = (node: TreemapNode): boolean =>
  node.other && node.id !== ROOT_OTHER_ID;

// The selection that opens a class's products in Explore: the scope plus
// the class, which counts every product under it or any class beneath it. A
// branch `other` has no filter, and a gap holds nothing to open.
export const classSelection = (
  selection: AnalyticsSelection,
  node: TreemapNode,
): AnalyticsSelection | null => {
  if (isBranchOther(node) || node.gap || node.product_count === 0) {
    return null;
  }
  return { ...selectionOf(selection), category: [node.category.id] };
};

// How a class reads: its name, or the words for the `other` nodes
export const classLabel = (node: TreemapNode): string =>
  node.category.name ?? node.id;

// What the hover title and the accessible name of a rectangle say
export const rectTitle = (rect: TreemapRect, parentLabel: string | null): string => {
  if (rect.own) {
    return `${counted(rect.count, "product")} filed on ${parentLabel ?? "the class"} itself`;
  }
  if (rect.gap) {
    return `${rect.label}: nothing on record`;
  }
  return `${rect.label}: ${counted(rect.count, "product")} · ${counted(rect.makers, "maker")} · ${counted(rect.articles, "article")}`;
};

export const rectName = (rect: TreemapRect, parentLabel: string | null): string => {
  if (rect.own) {
    return `filed here on ${parentLabel ?? "the class"}, ${counted(rect.count, "product")}`;
  }
  if (rect.gap) {
    return `${rect.label}, nothing on record`;
  }
  return `${rect.label}, ${counted(rect.count, "product")}`;
};
